import React, { useState } from 'react';
import { History, X, GitCompare, RotateCcw, Save } from 'lucide-react';
import { describeVacancySnapshotChanges } from '../utils/vacancyChangeDiff';
import type { VacancySnapshotForDiff } from '../utils/vacancyChangeDiff';
import {
    snapshotToVacancySnapshotForDiff,
    pickIntakeSnapshotFromUpdates,
    intakeSnapshotForHistoryDiff,
    type VacancyIntakeVersionEntry,
} from '../utils/vacancyIntakeVersions';
import { intakeSnapshotTextDiffSummaries, mergeStructuralAndTextDiffLines } from '../utils/intakeTextDiff';

export type IntakeVersionHistoryModalProps = {
    open: boolean;
    onClose: () => void;
    /** Newest first */
    versionsNewestFirst: VacancyIntakeVersionEntry[];
    /** Current state for “vs huidig” diff */
    pendingDiffSnap: VacancySnapshotForDiff;
    isReadOnly?: boolean;
    emptyHint?: string;
    onRestoreInForm?: (entry: VacancyIntakeVersionEntry) => void;
    onRestoreAndSave?: (entry: VacancyIntakeVersionEntry) => void;
};

const IntakeVersionHistoryModal: React.FC<IntakeVersionHistoryModalProps> = ({
    open,
    onClose,
    versionsNewestFirst,
    pendingDiffSnap,
    isReadOnly = false,
    emptyHint,
    onRestoreInForm,
    onRestoreAndSave,
}) => {
    const [expandedId, setExpandedId] = useState<string | null>(null);

    if (!open) return null;

    const close = () => {
        setExpandedId(null);
        onClose();
    };

    return (
        <div
            className="fixed inset-0 z-[650] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4"
            onClick={close}
        >
            <div
                className="bg-white w-full max-w-2xl max-h-[88vh] rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-slate-200"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="px-5 py-4 border-b border-slate-100 flex items-start justify-between gap-3 shrink-0">
                    <div>
                        <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest flex items-center gap-2">
                            <History size={16} className="text-[#e51b23]" />
                            Intake-geschiedenis
                        </h3>
                        <p className="text-[11px] text-slate-500 mt-1">
                            Checkpoints van stap 1 (Intake). Vergelijk met een oudere versie of zet intake-gegevens
                            Ook vanaf de Vacature Builder (stap Templates) te openen.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={close}
                        className="p-2 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors"
                        aria-label="Sluiten"
                    >
                        <X size={20} />
                    </button>
                </div>
                <div className="p-4 overflow-y-auto flex-1 min-h-0">
                    {versionsNewestFirst.length === 0 ? (
                        <p className="text-sm text-slate-600 leading-relaxed">
                            {emptyHint ||
                                'Nog geen checkpoints. Ga naar stap 1 Intake, vul de velden in en klik Opslaan — daarna verschijnt hier de eerste versie (ook als er geen automatische diff-regels zijn).'}
                        </p>
                    ) : (
                        <ul className="space-y-3">
                            {versionsNewestFirst.map((entry, idx) => {
                                const expanded = expandedId === entry.id;
                                const older = versionsNewestFirst[idx + 1];
                                const vsPrev = older
                                    ? mergeStructuralAndTextDiffLines(
                                          intakeSnapshotTextDiffSummaries(older.snapshot, entry.snapshot),
                                          describeVacancySnapshotChanges(
                                              snapshotToVacancySnapshotForDiff(older.snapshot),
                                              snapshotToVacancySnapshotForDiff(entry.snapshot)
                                          )
                                      )
                                    : [];
                                const pendingIntakeSnap = pickIntakeSnapshotFromUpdates({
                                    title: pendingDiffSnap.title ?? '',
                                    description: pendingDiffSnap.description ?? '',
                                    status: (pendingDiffSnap.status as string | undefined) || undefined,
                                    opdrachtgever_id: pendingDiffSnap.opdrachtgever_id ?? null,
                                    eindklant_id: pendingDiffSnap.eindklant_id ?? null,
                                    shared_with_users: pendingDiffSnap.shared_with_users,
                                    shared_with_clients: pendingDiffSnap.shared_with_clients,
                                    data: (pendingDiffSnap.data || {}) as Record<string, unknown>,
                                });
                                const vsForm = mergeStructuralAndTextDiffLines(
                                    intakeSnapshotTextDiffSummaries(
                                        intakeSnapshotForHistoryDiff(entry.snapshot),
                                        intakeSnapshotForHistoryDiff(pendingIntakeSnap)
                                    ),
                                    describeVacancySnapshotChanges(
                                        snapshotToVacancySnapshotForDiff(intakeSnapshotForHistoryDiff(entry.snapshot)),
                                        snapshotToVacancySnapshotForDiff(intakeSnapshotForHistoryDiff(pendingIntakeSnap))
                                    )
                                );
                                return (
                                    <li key={entry.id} className="border border-slate-200 rounded-xl overflow-hidden bg-white">
                                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 p-3 bg-slate-50/90">
                                            <div className="min-w-0">
                                                <div className="text-xs font-black text-slate-900">
                                                    {new Date(entry.savedAt).toLocaleString('nl-NL', {
                                                        dateStyle: 'short',
                                                        timeStyle: 'short',
                                                    })}
                                                </div>
                                                <div className="text-[11px] text-slate-500 truncate">
                                                    {entry.userName || entry.userEmail || 'Onbekende gebruiker'}
                                                </div>
                                            </div>
                                            <div className="flex flex-wrap gap-1.5 shrink-0">
                                                <button
                                                    type="button"
                                                    onClick={() => setExpandedId(expanded ? null : entry.id)}
                                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-slate-200 bg-white text-[10px] font-black uppercase tracking-widest text-slate-700 hover:border-slate-300 transition-colors"
                                                >
                                                    <GitCompare size={12} />
                                                    {expanded ? 'Verberg' : 'Wijzigingen'}
                                                </button>
                                                {!isReadOnly && onRestoreInForm && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (
                                                                !window.confirm(
                                                                    'Het formulier wordt gevuld met deze versie. Niet-opgeslagen wijzigingen gaan verloren. Doorgaan?'
                                                                )
                                                            )
                                                                return;
                                                            onRestoreInForm(entry);
                                                        }}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-amber-200 bg-amber-50 text-[10px] font-black uppercase tracking-widest text-amber-900 hover:bg-amber-100 transition-colors"
                                                    >
                                                        <RotateCcw size={12} />
                                                        Herstel
                                                    </button>
                                                )}
                                                {!isReadOnly && onRestoreAndSave && (
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            if (
                                                                !window.confirm(
                                                                    'Deze versie wordt teruggezet en direct opgeslagen. Doorgaan?'
                                                                )
                                                            )
                                                                return;
                                                            onRestoreAndSave(entry);
                                                        }}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg border border-[#e51b23]/30 bg-red-50 text-[10px] font-black uppercase tracking-widest text-[#B71C1C] hover:bg-red-100 transition-colors"
                                                    >
                                                        <Save size={12} />
                                                        Herstel &amp; sla
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                        <div className="px-3 py-2 border-t border-slate-100/80">
                                            <ul className="list-disc ml-4 text-[11px] text-slate-600 space-y-0.5">
                                                {entry.changeSummaries.slice(0, 20).map((s, i) => (
                                                    <li key={i}>{s}</li>
                                                ))}
                                            </ul>
                                        </div>
                                        {expanded && (
                                            <div className="border-t border-slate-200 p-3 space-y-4 text-xs bg-white">
                                                <div>
                                                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5 flex items-center gap-1">
                                                        <GitCompare size={12} /> T.o.v. vorige checkpoint
                                                    </div>
                                                    {vsPrev.length === 0 ? (
                                                        <p className="text-slate-400 italic">Dit is de eerste opgeslagen versie.</p>
                                                    ) : (
                                                        <ul className="list-disc ml-4 text-slate-700 space-y-0.5">
                                                            {vsPrev.map((line, i) => (
                                                                <li key={i}>{line}</li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                                <div>
                                                    <div className="text-[10px] font-black uppercase tracking-widest text-slate-400 mb-1.5">
                                                        T.o.v. huidige inhoud (inclusief niet-opgeslagen)
                                                    </div>
                                                    {vsForm.length === 0 ? (
                                                        <p className="text-emerald-700 font-medium">
                                                            Geen verschil met deze checkpoint.
                                                        </p>
                                                    ) : (
                                                        <ul className="list-disc ml-4 text-slate-700 space-y-0.5">
                                                            {vsForm.map((line, i) => (
                                                                <li key={i}>{line}</li>
                                                            ))}
                                                        </ul>
                                                    )}
                                                </div>
                                            </div>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>
            </div>
        </div>
    );
};

export default IntakeVersionHistoryModal;
